import rulesArchivesTemplate from './classic-guide-templates/rules-archives.htm?raw';
import rulesCombatTemplate from './classic-guide-templates/rules-combat.htm?raw';
import rulesCommunicateTemplate from './classic-guide-templates/rules-communicate.htm?raw';
import rulesDiscordTemplate from './classic-guide-templates/rules-discord.htm?raw';
import rulesEvolvingItemsTemplate from './classic-guide-templates/rules-evolving-items.htm?raw';
import rulesHonorTemplate from './classic-guide-templates/rules-honor.htm?raw';
import rulesIntroTemplate from './classic-guide-templates/rules-intro.htm?raw';
import rulesMilitaryTemplate from './classic-guide-templates/rules-military.htm?raw';
import rulesMarketTemplate from './classic-guide-templates/rules-market.htm?raw';
import rulesMonarchTemplate from './classic-guide-templates/rules-monarch.htm?raw';
import rulesMysticTemplate from './classic-guide-templates/rules-mystic.htm?raw';
import rulesOverviewTemplate from './classic-guide-templates/rules-overview.htm?raw';
import rulesRacesTemplate from './classic-guide-templates/rules-races.htm?raw';
import rulesScienceTemplate from './classic-guide-templates/rules-science.htm?raw';
import rulesServantsTemplate from './classic-guide-templates/rules-servants.htm?raw';
import rulesSittingTemplate from './classic-guide-templates/rules-sitting.htm?raw';
import rulesThieveryTemplate from './classic-guide-templates/rules-thievery.htm?raw';
import rulesTradeTemplate from './classic-guide-templates/rules-trade.htm?raw';
import rulesWarChestTemplate from './classic-guide-templates/rules-war-chest.htm?raw';
import originalGuideTemplate from './classic-guide-original.htm?raw';

export type ClassicGuideMacro =
  | 'egghatchtable'
  | 'evolutiontable'
  | 'familiarstartertable'
  | 'itemtable'
  | 'listops'
  | 'perstable'
  | 'racetable'
  | 'spelltable'
  | 'thievetable';

export type ClassicGuideBlock =
  | { type: 'heading'; level: 2 | 3; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'paragraph'; text: string }
  | { type: 'table'; rows: string[][] }
  | { type: 'generated'; args: string[]; label: string; macro: ClassicGuideMacro };

export type ClassicGuidePage = {
  id: string;
  title: string;
  subtitle: string;
  iconCode: string;
  template: string;
};

function extractOriginalSection(title: string, nextTitle?: string): string {
  const start = originalGuideTemplate.indexOf(`|mainhead('${title}'`);
  if (start < 0) {
    return '';
  }

  const bodyStart = originalGuideTemplate.indexOf('|smallhead', start);
  const nextStart = nextTitle
    ? originalGuideTemplate.indexOf(`|mainhead('${nextTitle}'`, bodyStart)
    : originalGuideTemplate.indexOf('</div>', bodyStart);

  return originalGuideTemplate
    .slice(bodyStart > -1 ? bodyStart : start, nextStart > -1 ? nextStart : undefined)
    .trim();
}

const kingdomRelationsTemplate = extractOriginalSection('Kingdom Relations', 'Trade & Aid');
export const classicGuidePages: ClassicGuidePage[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    subtitle: 'Here is where your adventure begins.',
    iconCode: 'introduction',
    template: rulesIntroTemplate,
  },
  {
    id: 'races-personalities-advisors',
    title: 'Races, Personalities & Advisors',
    subtitle: 'Picking your province.',
    iconCode: 'province_bonus',
    template: rulesRacesTemplate,
  },
  {
    id: 'economics-growth',
    title: 'Economics & Growth',
    subtitle: 'Looking after your province.',
    iconCode: 'economics',
    template: rulesOverviewTemplate,
  },
  {
    id: 'military',
    title: 'Military',
    subtitle: 'Building your armies.',
    iconCode: 'military',
    template: rulesMilitaryTemplate,
  },
  {
    id: 'combat',
    title: 'Combat',
    subtitle: 'Expansion and retaliation.',
    iconCode: 'combat',
    template: rulesCombatTemplate,
  },
  {
    id: 'mystics',
    title: 'Mystics',
    subtitle: 'Mastering the magical arts.',
    iconCode: 'mystics',
    template: rulesMysticTemplate,
  },
  {
    id: 'thievery',
    title: 'Thievery',
    subtitle: 'Covert operations.',
    iconCode: 'thievery',
    template: rulesThieveryTemplate,
  },
  {
    id: 'science',
    title: 'Science',
    subtitle: 'Research and development.',
    iconCode: 'research',
    template: rulesScienceTemplate,
  },
  {
    id: 'nobility-honor',
    title: 'Nobility & Honor',
    subtitle: 'From peasant to prince.',
    iconCode: 'honor',
    template: rulesHonorTemplate,
  },
  {
    id: 'kingdom-intelligence',
    title: 'Kingdom Intelligence',
    subtitle: 'Planning and archives.',
    iconCode: 'intel',
    template: rulesArchivesTemplate,
  },
  {
    id: 'kingdom-monarchy',
    title: 'Kingdom Monarchy',
    subtitle: 'Building a cohesive kingdom.',
    iconCode: 'kingdom',
    template: rulesMonarchTemplate,
  },
  {
    id: 'sitting',
    title: 'Sitting',
    subtitle: 'Looking after provinces while rulers are away.',
    iconCode: 'sitting',
    template: rulesSittingTemplate,
  },
  {
    id: 'communication',
    title: 'Communication',
    subtitle: 'Chat and message options.',
    iconCode: 'chat',
    template: rulesCommunicateTemplate,
  },
  {
    id: 'discord',
    title: 'Discord Integration',
    subtitle: 'Official bot, slash commands, alerts and kingdom webhooks.',
    iconCode: 'chat',
    template: rulesDiscordTemplate,
  },
  {
    id: 'kingdom-relations',
    title: 'Kingdom Relations',
    subtitle: 'Stances, relations, and war.',
    iconCode: 'relations',
    template: kingdomRelationsTemplate,
  },
  {
    id: 'trade-aid',
    title: 'Trade & Aid',
    subtitle: 'Helping your kingdom.',
    iconCode: 'aid',
    template: rulesTradeTemplate,
  },
  {
    id: 'war-chest',
    title: 'War Chest',
    subtitle: 'Storing and rotating special items.',
    iconCode: 'war_chest',
    template: rulesWarChestTemplate,
  },
  {
    id: 'gems-market',
    title: 'Gems and The Market',
    subtitle: 'Collecting gems and special items.',
    iconCode: 'the_arcane_market',
    template: rulesMarketTemplate,
  },
  {
    id: 'evolving-items',
    title: 'Evolving Items',
    subtitle: 'Growing familiars into stronger forms.',
    iconCode: 'province_bonus',
    template: rulesEvolvingItemsTemplate,
  },
  {
    id: 'servants',
    title: 'Servants',
    subtitle: 'Support the game and build a permanent gem reserve.',
    iconCode: 'sitting',
    template: rulesServantsTemplate,
  },
];

export function findClassicGuidePage(pageId: string): ClassicGuidePage | undefined {
  return classicGuidePages.find(page => page.id === pageId);
}

function decodeClassicText(value: string): string {
  return value
    .replaceAll('{srvr}', 'Mythea')
    .replaceAll('&#39;', "'")
    .replaceAll('&quot;', '"')
    .replaceAll('&amp;', '&')
    .replaceAll('&nbsp;', ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripTemplateNoise(value: string): string {
  return value
    .replace(/\|fullbut\([^|]*\)\|/g, ' ')
    .replace(/\|(?:tabstfull|tabst|tabend|result|ltemp)\([^|]*\)\|/g, ' ')
    .replace(/\|template\[[^\]]+\]\|/g, ' ')
    .replace(/<\/?div[^>]*>/gi, ' ')
    .replace(/<\/?strong[^>]*>/gi, ' ')
    .replace(/<\/?center[^>]*>/gi, ' ')
    .replace(/\r/g, '');
}

function appendTextBlocks(blocks: ClassicGuideBlock[], value: string): void {
  const text = stripTemplateNoise(value)
    .replace(/<br\s*\/?>/gi, '\n\n')
    .replace(/\n{3,}/g, '\n\n');

  for (const segment of text.split(/\n\s*\n/)) {
    const decoded = decodeClassicText(segment);
    if (decoded) {
      if (decoded.startsWith('- ')) {
        const previousBlock = blocks.at(-1);
        const item = decoded.replace(/^-\s+/, '');
        if (previousBlock?.type === 'list') {
          previousBlock.items.push(item);
        } else {
          blocks.push({ type: 'list', items: [item] });
        }
        continue;
      }

      blocks.push({ type: 'paragraph', text: decoded });
    }
  }
}

function parseClassicTable(value: string, width: number, delimiter = '/'): string[][] {
  const cells = value
    .replace(/<br\s*\/?>/gi, '\n')
    .split(delimiter)
    .map(cell => decodeClassicText(cell))
    .filter(Boolean);

  if (cells.length <= 2) {
    return [cells];
  }

  const columnCount = Number.isFinite(width) && width > 0 ? Math.trunc(width) : 2;
  const rows: string[][] = [];
  for (let index = 0; index < cells.length; index += columnCount) {
    rows.push(cells.slice(index, index + columnCount));
  }
  return rows;
}

export function parseClassicGuideTemplate(template: string): ClassicGuideBlock[] {
  const blocks: ClassicGuideBlock[] = [];
  const macroPattern =
    /\|(smallhead|mainhead)\('([^']*)'[^|]*\)\||\|nicetable\("([\s\S]*?)",(\d+)(?:,\s*"([^"]*)")?\)\||\|(listops|racetable|perstable|spelltable|thievetable|itemtable|evolutiontable|egghatchtable|familiarstartertable)\(([^)]*)\)\|/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = macroPattern.exec(template)) !== null) {
    appendTextBlocks(blocks, template.slice(cursor, match.index));

    if (match[1]) {
      blocks.push({
        type: 'heading',
        level: match[1] === 'mainhead' ? 2 : 3,
        text: decodeClassicText(match[2]),
      });
    } else if (match[3]) {
      blocks.push({ type: 'table', rows: parseClassicTable(match[3], Number(match[4]), match[5] || '/') });
    } else if (match[6]) {
      const macro = match[6] as ClassicGuideMacro;
      blocks.push({
        type: 'generated',
        macro,
        args: `${match[7] || ''}`
          .split(',')
          .map(arg => arg.trim())
          .filter(Boolean),
        label: `${match[6].replace('table', '') || 'Generated'} reference table`,
      });
    }

    cursor = macroPattern.lastIndex;
  }

  appendTextBlocks(blocks, template.slice(cursor));
  return blocks;
}
